import GridViewIcon from '@mui/icons-material/GridView'
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined'
import Logout from '@mui/icons-material/Logout'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import PersonIcon from '@mui/icons-material/Person'
import {
  Avatar,
  Badge,
  Box,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Toolbar,
  Typography
} from '@mui/material'
import { t } from 'i18next'
import { MouseEvent, useEffect, useState } from 'react'
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useParams
} from 'react-router'
import logo from '../assets/images/logo.svg'
import { useAuthStore } from '../hooks/useAuthStore'
import { useSignOutMutation } from '../hooks/useMutations/useAuthMutations'
import { useNotifications, useUser } from '../hooks/useQueries/useUsers'
import { useWorkspace, useWorkspaces } from '../hooks/useQueries/useWorkspaces'
import { getAvatarName } from '../utils/user.utils'
import ContainedAvatar from './ContainedAvatar'
import NotificationsMenu from './NotificationsMenu'

export default function HeaderWorkspace() {
  const params = useParams()
  const navigate = useNavigate()
  const companyId = params.companyId!
  const workspaceId = params.workspaceId!
  const { email, userId, accessToken } = useAuthStore()
  const { data: user, isLoading } = useUser(userId!)
  const { data: notifications } = useNotifications(userId!, {
    pageSize: '1',
    pageNumber: '1'
  })
  const { data: workspaces } = useWorkspaces(companyId)
  const { data: workspace } = useWorkspace(companyId, workspaceId)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [workspaceSelected, setWorkspaceSelected] = useState('')
  const open = Boolean(anchorEl)
  const location = useLocation()
  const [notificationsAnchorEl, setNotificationsAnchorEl] =
    useState<null | HTMLElement>(null)
  const isNotificationsMenuOpen = Boolean(notificationsAnchorEl)

  useEffect(() => {
    if (workspace?.name) {
      setWorkspaceSelected(workspace.name)
    }
  }, [workspace?.name])

  const signOutMutation = useSignOutMutation()

  if (isLoading || !user) {
    return <div>Loading...</div>
  }

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleOpenNotificationsMenu = (event: MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget)
  }

  const handleCloseNotificationsMenu = () => {
    setNotificationsAnchorEl(null)
  }

  return (
    <Grid container sx={{ backgroundColor: 'background.default' }}>
      <Paper
        sx={{
          width: '100%',
          backgroundColor: 'background.default',
          padding: '0.75rem 1.5rem'
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Box component={Link} to={'/companies'}>
            <img
              src={logo}
              alt='Company logo'
              style={{ width: '6.41581rem' }}
            />
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'flex-start',
              marginLeft: '12rem'
            }}
          >
            <Stack direction='row' spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='body1' sx={{ fontWeight: 500 }}>
                  {t('workspace.proyect')}
                </Typography>
                <Select
                  value={workspaceSelected}
                  onChange={e => {
                    const selectedWorkspaceName = e.target.value
                    setWorkspaceSelected(selectedWorkspaceName)

                    const selectedWorkspace = workspaces?.find(
                      workspace => workspace.name === selectedWorkspaceName
                    )

                    if (selectedWorkspace) {
                      navigate(
                        `/companies/${companyId}/workspaces/${selectedWorkspace?.id}/digital-twin`
                      )
                    }
                  }}
                  size='small'
                  sx={{
                    width: '22.25rem',
                    borderRadius: '.25rem'
                  }}
                >
                  {workspaces?.map(workspace => (
                    <MenuItem key={workspace.id} value={workspace.name}>
                      {workspace.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box
                  component={Link}
                  to='/companies'
                  sx={{
                    textDecoration: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <IconButton>
                    <GridViewIcon
                      sx={{ color: 'custom.semiTransparentWhite' }}
                    />
                  </IconButton>
                  <Typography
                    variant='caption'
                    sx={{ color: 'custom.semiTransparentWhite' }}
                  >
                    {t('workspace.companies')}
                  </Typography>
                </Box>

                <NavLink
                  to={`/companies/${companyId}/workspaces/${workspaceId}/members`}
                  style={{ textDecoration: 'none' }}
                >
                  {() => {
                    const extendedIsActive = [
                      `/companies/${companyId}/workspaces/${workspaceId}/members`,
                      `/companies/${companyId}/workspaces/${workspaceId}/roles`,
                      `/companies/${companyId}/workspaces/${workspaceId}/jobtitles`
                    ].some(path => location.pathname.startsWith(path))

                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                      >
                        <IconButton>
                          <Groups2OutlinedIcon
                            sx={{
                              color: extendedIsActive
                                ? 'primary.main'
                                : 'custom.semiTransparentWhite'
                            }}
                          />
                        </IconButton>
                        <Typography
                          variant='caption'
                          sx={{
                            color: extendedIsActive
                              ? 'primary.main'
                              : 'custom.semiTransparentWhite'
                          }}
                        >
                          {t('workspace.members')}
                        </Typography>
                      </Box>
                    )
                  }}
                </NavLink>

                <NavLink
                  to={`/companies/${companyId}/workspaces/${workspaceId}/invitations/sent`}
                  style={{ textDecoration: 'none' }}
                >
                  {() => {
                    const extendedIsActive = [
                      `/companies/${companyId}/workspaces/${workspaceId}/invitations/sent`,
                      `/companies/${companyId}/workspaces/${workspaceId}/invitations/received`
                    ].some(path => location.pathname.startsWith(path))

                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                      >
                        <IconButton>
                          <MailOutlineIcon
                            sx={{
                              color: extendedIsActive
                                ? 'primary.main'
                                : 'custom.semiTransparentWhite'
                            }}
                          />
                        </IconButton>
                        <Typography
                          variant='caption'
                          sx={{
                            color: extendedIsActive
                              ? 'primary.main'
                              : 'custom.semiTransparentWhite'
                          }}
                        >
                          {t('workspace.invitations')}
                        </Typography>
                      </Box>
                    )
                  }}
                </NavLink>
              </Box>
            </Stack>
          </Box>

          <Stack direction='row' spacing='1rem'>
            <IconButton onClick={handleOpenNotificationsMenu}>
              <Badge
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                color='warning'
                badgeContent={notifications?.totalUnread || 0}
              >
                <NotificationsOutlinedIcon
                  fontSize='large'
                  sx={{
                    color: isNotificationsMenuOpen
                      ? 'primary.main'
                      : 'custom.semiTransparentWhite'
                  }}
                />
              </Badge>
            </IconButton>
            <NotificationsMenu
              anchorEl={notificationsAnchorEl}
              isOpen={isNotificationsMenuOpen}
              handleCloseMenu={handleCloseNotificationsMenu}
            />
            <Stack direction='row' spacing='.75rem' alignItems='center'>
              {user.avatarUri ? (
                <ContainedAvatar
                  src={user.avatarUri}
                  sx={{ marginTop: '15px' }}
                />
              ) : (
                <Avatar sx={{ marginTop: '15px' }}>
                  {getAvatarName(user.name!, user.lastName!)}
                </Avatar>
              )}

              <Box onClick={handleClick} sx={{ cursor: 'pointer' }}>
                <Stack direction='column'>
                  <Typography variant='body1'>
                    {user.name} {user.lastName}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {email}
                  </Typography>
                </Stack>
              </Box>

              <Menu
                anchorEl={anchorEl}
                id='account-menu'
                open={open}
                onClose={handleClose}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      width: '290px',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))'
                    }
                  }
                }}
              >
                <MenuItem component={Link} to='/profile' onClick={handleClose}>
                  <ListItemIcon>
                    <PersonIcon fontSize='small' />
                  </ListItemIcon>
                  {t('account.title')}
                </MenuItem>
                <MenuItem
                  onClick={() => signOutMutation.mutate(accessToken!)}
                  disabled={signOutMutation.isPending}
                >
                  <ListItemIcon>
                    <Logout fontSize='small' />
                  </ListItemIcon>
                  {t('sign_out')}
                </MenuItem>
              </Menu>
            </Stack>
          </Stack>
        </Toolbar>
      </Paper>
    </Grid>
  )
}
